import {
  all,
  call,
  put,
  takeLatest,
  take,
  takeEvery,
} from "redux-saga/effects";
import {
  signInGoogle,
  signInEmail,
  getUserAuthInfo,
  logOut,
} from "../../api/auth";
import {
  initUserDoc,
  getUserInfo,
  updateUserInfo,
  addAttachedFile,
  getAttachedFiles,
  getUserDocRef,
} from "../../api/firestore/user";
import { initUserSearchDoc } from "../../api/firestore/search";
import { uploadProfilePicture, uploadAttachedFile } from "../../api/storage";
import {
  sagaApiActionsConstructor,
  sagaApiListenerConstructor,
  createFirestoreDocumentChannel,
} from "../helpers";

const { requestError, requestSuccess, requestStart } =
  sagaApiActionsConstructor("main");

const apiListeners = sagaApiListenerConstructor("main");

// ___USER___
function* signInWithEmailSaga(action) {
  let res = {};

  try {
    const { email, password } = action.payload;
    const authRes = yield signInEmail(email, password);

    if (!authRes?.user?.uid) {
      yield requestError("No se ha podido completar el registro");
      return;
    }

    yield requestSuccess("/sign-in/profile-config");
  } catch (e) {
    console.error(e);
    yield requestError("No se ha podido completar el registro");
  }
}

function* signInWithGoogleSaga() {
  try {
    const authRes = yield call(signInGoogle);
    const uid = authRes?.user?.uid || false;

    if (!uid) {
      yield requestError("No hemos podido registrarte con google");
      return;
    }

    const userInfo = yield call(getUserInfo);

    if (!userInfo) {
      yield requestSuccess("/sign-in/profile-config");
      return;
    }

    yield put({ type: "main/init", payload: userInfo });
    yield requestSuccess("/home");
  } catch (e) {
    console.log(e);
  }
}

function* logInEmailSaga(action) {
  try {
    const { email, password } = action.payload;
    const authRes = yield signInEmail(email, password);

    if (authRes?.user?.uid) {
      yield requestSuccess("/home");
    } else {
      yield requestError("Los datos intruducidos no son correctos");
    }
  } catch (e) {
    yield requestError("Los datos intruducidos no son correctos");
  }
}

function* fetchUserInfo() {
  try {
    const userRef = yield call(getUserDocRef);
    const userDoc = yield call(getUserInfo);

    if (!userDoc) {
      const userAuthData = yield call(getUserAuthInfo);
      const finalData = { email: userAuthData?.email };

      yield put({ type: "main/init", payload: finalData });
      yield put({ type: "chat/init", payload: { chats: finalData?.chats } });
      yield requestSuccess("/sign-in/profile-config");

      return;
    }

    const channel = yield call(createFirestoreDocumentChannel, userRef);

    while (true) {
      const { data, error } = yield take(channel);

      yield requestStart();

      if (data) {
        const userAuthData = yield call(getUserAuthInfo);
        const attachments = yield call(getAttachedFiles);
        const finalData = { email: userAuthData?.email, ...data, attachments };

        yield put({ type: "main/init", payload: finalData });
        yield put({
          type: "chat/init",
          payload: {
            chats: finalData?.chats,
            chatsRequests: finalData?.chatRequests,
          },
        });

        yield requestSuccess();
      }

      if (error) {
        console.log(error);
        yield requestError("No pudimos recuperar tu información");
      }
    }
  } catch (e) {}
}

function* initUserInfoDatabaseSaga(action) {
  try {
    const { payload } = action;

    let data = {
      name: payload?.name,
      surname: payload?.surname,
      university: payload?.university,
      investigationLines: payload?.investigationLines,
      investigationGroup: payload?.investigationGroup,
      profilePicture: null,
      chats: [],
      chatRequests: [],
    };

    if (payload?.profilePicture?.data) {
      const url = yield call(uploadProfilePicture, payload.profilePicture);
      data.profilePicture = url;
    }

    yield call(initUserDoc, data);
    // yield call(initUserSearchDoc, data);

    yield put({
      type: "main/init",
      payload: data,
    });

    yield requestSuccess("/home");
  } catch (e) {
    yield requestError("No pudimos completar tu operación");
    console.log(e);
  }
}

function* logOutUserSaga() {
  try {
    yield call(logOut);
    yield requestSuccess("/sign-in");
  } catch (e) {
    console.log(e);
    yield requestError("No hemos podido completar la operación");
  }
}

function* uploadProfileImageSaga({ payload }) {
  try {
    yield requestStart();
    const downloadUrl = yield call(uploadProfilePicture, payload);

    yield call(updateUserInfo, { profilePicture: downloadUrl });

    yield put({ type: "main/updateProfilePicture", payload: downloadUrl });
    yield requestSuccess();
  } catch (e) {
    console.log(e);
    yield requestError(
      "No hemos podido subir la image, por favor inténtalo de nuevo"
    );
  }
}

function* uploadFileSaga({ payload }) {
  try {
    yield requestStart();
    const url = yield call(uploadAttachedFile, payload);

    yield call(addAttachedFile, {
      name: payload.name,
      type: payload.type,
      timestamp: Date.now(),
      url: url,
    });

    const attachments = yield call(getAttachedFiles);

    yield put({ type: "main/fetchUserInfo" });
    yield requestSuccess();
  } catch (e) {
    console.log(e);
    yield requestError(
      "No pudimos subir el archivo, por favor, inténtalo más tarde"
    );
  }
}

function* failUploadingImageSaga() {
  try {
    yield requestError("No hemos podido subir la imagen");
  } catch (e) {
    console.log(e);
  }
}

function* mainRootSaga() {
  yield all([
    takeLatest("main/signInWithEmail", signInWithEmailSaga),
    takeLatest("main/signInWithGoogle", signInWithGoogleSaga),
    takeLatest("main/logInEmail", logInEmailSaga),
    takeLatest("main/initUserInfoDatabase", initUserInfoDatabaseSaga),
    takeLatest("main/fetchUserInfo", fetchUserInfo),
    takeLatest("main/logOutUser", logOutUserSaga),
    takeLatest("main/uploadProfileImage", uploadProfileImageSaga),
    takeLatest("main/uploadFile", uploadFileSaga),
    takeLatest("main/failUploadingImage", failUploadingImageSaga),
    ...apiListeners,
  ]);
}

export default mainRootSaga;
